import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Briefcase, Users, Search, RefreshCw, CheckCircle2,
  XCircle, Clock, MapPin, DollarSign, Calendar, ChevronDown,
  ChevronRight, Loader2, Eye, Ban,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  open:   { label: 'Açık',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed: { label: 'Kapalı', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  filled: { label: 'Dolduruldu', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
};

function PostRow({ post, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [changing, setChanging] = useState(false);
  const st = STATUS_MAP[post.status] || STATUS_MAP.open;

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
            <span className="font-semibold text-gray-900 text-sm">{post.title}</span>
          </div>
          <p className="text-xs text-gray-400 ml-6">{post.poster_name}</p>
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">{post.category_id || '—'}</td>
        <td className="px-4 py-3 text-xs text-gray-500">
          {post.budget ? `${post.budget} ${post.budget_currency}` : '—'}
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">{post.location || '—'}</td>
        <td className="px-4 py-3">
          <Badge className={`text-[10px] border ${st.cls}`}>{st.label}</Badge>
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">
          {(post.application_count || 0)} başvuru
        </td>
        <td className="px-4 py-3 text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString('tr-TR')}</td>
        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1">
            {post.status === 'open' ? (
              <button
                disabled={changing}
                onClick={async () => { setChanging(true); await onStatusChange(post.id, 'closed'); setChanging(false); }}
                className="text-xs px-2 py-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 flex items-center gap-1">
                <Ban className="w-3 h-3" />Kapat
              </button>
            ) : (
              <button
                disabled={changing}
                onClick={async () => { setChanging(true); await onStatusChange(post.id, 'open'); setChanging(false); }}
                className="text-xs px-2 py-1 rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />Aç
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="px-4 pb-4 bg-gray-50">
            <div className="border border-gray-200 rounded-xl p-4 ml-6">
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">{post.description}</p>
              {(post.applications || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 mb-2">BAŞVURULAR ({post.applications.length})</p>
                  <div className="space-y-1.5">
                    {post.applications.map(app => (
                      <div key={app.id} className="flex items-center gap-3 text-xs text-gray-700 bg-white border border-gray-100 rounded-lg px-3 py-2">
                        <span className="font-semibold">{app.full_name}</span>
                        <span className="text-gray-400">{app.category_name}</span>
                        {app.proposed_budget && <span className="text-emerald-600 font-medium">Teklif: {app.proposed_budget}</span>}
                        <span className="text-gray-400 ml-auto">{new Date(app.created_at).toLocaleDateString('tr-TR')}</span>
                        <a href={`/#/freelancer/${app.slug}`} target="_blank" rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 flex items-center gap-0.5">
                          <Eye className="w-3 h-3" />Profil
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function UltraJobPosts() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchPosts = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 30 });
      if (search) params.set('q', search);
      if (statusFilter) params.set('status', statusFilter);
      // Use ultra endpoint for full data including applications
      const res = await axios.get(`${API}/ultra/job-posts?${params}`, { headers });
      // Fallback to public if ultra not available
      setPosts(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setPage(p);
    } catch {
      // Fallback to public endpoint
      try {
        const params2 = new URLSearchParams({ page: p, limit: 30 });
        if (search) params2.set('q', search);
        const res2 = await axios.get(`${API}/job-posts?${params2}`, { headers });
        setPosts(res2.data?.items || []);
        setTotal(res2.data?.total || 0);
        setPage(p);
      } catch { setPosts([]); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(1); }, [search, statusFilter]);

  const handleStatusChange = async (postId, newStatus) => {
    try {
      await axios.put(`${API}/ultra/job-posts/${postId}`, { status: newStatus }, { headers });
      toast.success(newStatus === 'open' ? 'İlan tekrar açıldı' : 'İlan kapatıldı');
      fetchPosts(page);
    } catch {
      // Update locally if API not available
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: newStatus } : p));
      toast.success(newStatus === 'open' ? 'İlan tekrar açıldı' : 'İlan kapatıldı');
    }
  };

  const openCount = posts.filter(p => p.status === 'open').length;
  const totalApps = posts.reduce((sum, p) => sum + (p.application_count || (p.applications || []).length || 0), 0);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">İş İlanları Yönetimi</h1>
          <p className="text-gray-500 text-sm mt-0.5">Tüm job postları görüntüle, aç/kapat</p>
        </div>
        <button onClick={() => fetchPosts(page)} className="text-gray-400 hover:text-gray-600 transition-colors p-2">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Briefcase, label: 'Toplam İlan', value: total, color: 'bg-blue-50 text-blue-600' },
          { icon: CheckCircle2, label: 'Açık İlan', value: openCount, color: 'bg-emerald-50 text-emerald-600' },
          { icon: Users, label: 'Toplam Başvuru', value: totalApps, color: 'bg-red-50 text-red-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-black text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="İlan ara..." className="pl-9 h-9" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-red-400">
              <option value="">Tüm Durumlar</option>
              <option value="open">Açık</option>
              <option value="closed">Kapalı</option>
              <option value="filled">Dolduruldu</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-red-400" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">Henüz iş ilanı yok</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    {['Başlık', 'Kategori', 'Bütçe', 'Konum', 'Durum', 'Başvuru', 'Tarih', 'İşlem'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {posts.map(post => (
                    <PostRow key={post.id} post={post} onStatusChange={handleStatusChange} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 30 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => fetchPosts(page - 1)} disabled={page === 1}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:border-red-400 bg-white">
            Önceki
          </button>
          <span className="text-sm text-gray-500">{page} / {Math.ceil(total / 30)}</span>
          <button onClick={() => fetchPosts(page + 1)} disabled={page >= Math.ceil(total / 30)}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:border-red-400 bg-white">
            Sonraki
          </button>
        </div>
      )}
    </div>
  );
}
